/** Custom field values of one object (product, collection…): one input per definition, saved with its own button. */
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Form, Input, InputNumber, Space, Switch, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import RichEditor from './RichEditor';
import { listOf, sitePath, toastErr, useLeaveGuard, useSite } from './common';

const { Text } = Typography;
const keyOf = (f) => `${f.namespace}.${f.key}`;
/** value as the form holds it (json as text) */
const toForm = (f) => {
    if (f.type === 'json') return f.value === null || f.value === undefined ? '' : JSON.stringify(f.value, null, 2);
    return f.value ?? (f.type === 'boolean' ? false : '');
};

const CustomFieldsCard = ({ owner, oid, style }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId } = useSite();
    const [items, setItems] = useState(null);
    const [vals, setVals] = useState({});
    const [saving, setSaving] = useState(false);
    const [bad, setBad] = useState({});

    const apply = (list) => {
        setItems(list);
        setVals(Object.fromEntries(list.map((f) => [keyOf(f), toForm(f)])));
        setBad({});
    };
    useEffect(() => {
        let live = true;
        api.get(`/metafields/${owner}/${oid}`, null, { skipGlobalErrorToast: true }).then((d) => live && apply(listOf(d))).catch(() => live && setItems([]));
        return () => { live = false; };
    }, [api, owner, oid]);

    const changed = useMemo(() => (items || []).filter((f) => vals[keyOf(f)] !== toForm(f)), [items, vals]);
    useLeaveGuard(changed.length > 0);

    const save = async () => {
        const values = {};
        const errs = {};
        changed.forEach((f) => {
            const k = keyOf(f);
            const v = vals[k];
            if (f.type === 'json' && String(v).trim() !== '') {
                try { JSON.parse(v); } catch (_) { errs[k] = t('Not valid JSON'); }
            }
            values[k] = v;
        });
        setBad(errs);
        if (Object.keys(errs).length) return;
        setSaving(true);
        try {
            const d = await api.put(`/metafields/${owner}/${oid}`, { values });
            apply(listOf(d));
            message.success(t('Saved'));
        } catch (e) { toastErr(e, t('Save failed')); } finally { setSaving(false); }
    };

    if (!items) return null;
    const settings = () => navigate(sitePath(siteId, 'settings/custom-fields'));
    if (!items.length) {
        return (
            <Card size="small" style={{ marginBottom: 16, ...style }}>
                <Text type="secondary">{t('Custom fields')}: </Text><a onClick={settings}>{t('Add a field in settings')}</a>
            </Card>
        );
    }
    const set = (k, v) => setVals((x) => ({ ...x, [k]: v }));
    const input = (f) => {
        const k = keyOf(f);
        const v = vals[k];
        switch (f.type) {
            case 'number': return <InputNumber value={v === '' ? null : v} onChange={(x) => set(k, x ?? '')} style={{ width: '100%', maxWidth: 240 }} />;
            case 'boolean': return <Switch checked={!!v} onChange={(x) => set(k, x)} />;
            case 'date': return <DatePicker value={v ? dayjs(v) : null} onChange={(d) => set(k, d ? d.format('YYYY-MM-DD') : '')} />;
            case 'url': return <Input value={v} placeholder="https://…" onChange={(e) => set(k, e.target.value)} />;
            case 'json': return <Input.TextArea value={v} autoSize={{ minRows: 2, maxRows: 10 }} style={{ fontFamily: 'monospace', fontSize: 12 }} onChange={(e) => set(k, e.target.value)} />;
            case 'rich_text': return <RichEditor value={v} onChange={(x) => set(k, x)} />;
            default: return <Input value={v} onChange={(e) => set(k, e.target.value)} />;
        }
    };
    return (
        <Card title={t('Custom fields')} style={{ marginBottom: 16, ...style }}
            extra={<Space><a onClick={settings} style={{ fontSize: 12 }}>{t('Manage')}</a><Button size="small" type={changed.length ? 'primary' : 'default'} disabled={!changed.length} loading={saving} onClick={save}>{t('Save')}</Button></Space>}>
            <Form layout="vertical">
                {items.map((f, i) => (
                    <Form.Item key={keyOf(f)} label={f.name} style={i === items.length - 1 ? { marginBottom: 0 } : undefined}
                        validateStatus={bad[keyOf(f)] ? 'error' : undefined} help={bad[keyOf(f)]}>
                        {input(f)}
                    </Form.Item>
                ))}
            </Form>
        </Card>
    );
};

export default CustomFieldsCard;
